import {
  Box,
  color,
  Flex,
  Modal,
  PrimaryHeading,
  Spinner,
  useColorMode,
  useIsMobileView,
} from 'deepstash-ui';
import { UsersResponse } from 'api/api.types';
import useInfiniteScroll from 'hooks/useInfiniteScroll';
import React, { useEffect, useMemo, useRef } from 'react';
import scrollbar from 'theme/Scrollbar.module.scss';
import { UserData, UsersModalType } from 'types';
import { normalizeUserData } from 'utils/normalizers/profile';
import UserFollowRow from 'src/components/user/UserFollowRow';
import { Analytics, Events } from 'src/services/analytics';

interface UsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  id?: number | string;
  /**
   * Setting usecase will let the modal handle the data fetching based on the picked usecase
   */
  usecase?: UsersModalType;
  /**
   * Users to be listed in the modal
   * Use this if none of the predefined usecases is suitable
   */
  data?: UserData[];
  /**
   * Override the title of the modal if the usecase one is not suited or no usecase is provided
   */
  title?: string;
  updateFollowingNum?: (isFollowing: boolean) => void;
}

const UsersModal: React.FC<UsersModalProps> = ({
  isOpen,
  onClose,
  id,
  usecase,
  data: dataProp,
  title: titleProp,
  updateFollowingNum,
}) => {
  const { colorMode } = useColorMode();
  const isMobileView = useIsMobileView();

  const USER_API_QUERY_SIZE = 20;

  const target = useRef<HTMLDivElement>(null);

  const initialUrl = useMemo(() => {
    switch (usecase) {
      case 'saves':
        return `/user/saved_block/${id}/`;
      case 'followers':
        return `/user/${id}/followers/`;
      case 'following':
        return `/user/${id}/following/`;
      // [Like functionality]
      // case 'likes':
      //   return `/user/liked_block/${id}/`;
      case 'top-stashers':
        return `/hashtag/top_stashers/${id}/`;
      default:
        return '';
    }
  }, [usecase, id]);

  const { data, isFetching, isFirstLoading } = useInfiniteScroll<UsersResponse>(
    {
      firstPageUrl: initialUrl,
      queryKey: ['userModal', usecase, id],
      getKey: (_index, previousPageData) => {
        if (!isOpen) return undefined;

        // reached the end
        if (previousPageData && previousPageData.objects.length === 0)
          return undefined;

        return previousPageData.meta.next?.replace('/api/v2', '');
      },
      target,
    },
  );

  const modalTitle = useMemo(() => {
    if (titleProp) {
      return titleProp;
    }
    switch (usecase) {
      case 'saves':
        return 'People who stashed this idea';
      case 'followers':
        return 'Followers';
      case 'following':
        return 'Following';
      // [Like functionality]
      // case 'likes':
      //   return 'People who liked this idea';
      default:
        return '';
    }
  }, [usecase, titleProp]);

  const users = useMemo(() => {
    return data.length === 0
      ? dataProp ?? []
      : data.map(users => users.objects.map(normalizeUserData)).flat();
  }, [data, dataProp]);

  useEffect(() => {
    // Log the loaded page whenever a new page is loaded.
    // Nothing will be logged for those with no followers.
    if (users.length != 0)
      Analytics.logEvent({
        eventName: Events.modals.usersModal,
        properties: {
          type: usecase ?? 'saves',
          pageCount: Math.ceil(users.length / USER_API_QUERY_SIZE),
        },
        platforms: ['amplitude'],
      });
  }, [users.length]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      borderRadius="15px"
      backgroundColor={color[colorMode].surface}
      height="60vh"
      maxWidth="28rem"
      width={{ base: 'calc(100% - 1rem)', lg: '40vw' }}
      modalBodyProps={{
        className:
          colorMode === 'light'
            ? scrollbar.scrollVerticalLightSurface
            : scrollbar.scrollVerticalDarkSurface,
      }}
    >
      <Flex
        color={color[colorMode].text}
        width="95%"
        borderRadius="15px 15px 0 0"
        top={0}
        left={0}
        paddingLeft="2rem"
        paddingTop="2rem"
        justifyContent="flex-start"
        position="absolute"
        zIndex={1}
        backgroundColor={color[colorMode].surface}
      >
        <PrimaryHeading
          size="h2"
          textSize={isMobileView ? 's' : 'm'}
          type="bold"
        >
          {modalTitle}
        </PrimaryHeading>
      </Flex>
      <Box mt="3rem">
        {users.map(user => (
          <UserFollowRow
            user={user}
            onUserClick={onClose}
            onFollowSuccess={updateFollowingNum}
            usecase={usecase}
            key={`users-modal-user-row-${user.id}`}
          />
        ))}
        {!isFirstLoading && isFetching && (
          <Flex justifyContent="center">
            <Spinner color={color[colorMode].textSecondary} />
          </Flex>
        )}
        <Box h="50px" ref={target} />
      </Box>
    </Modal>
  );
};

export default UsersModal;
